import React, { CSSProperties, ChangeEvent, useState, useEffect, useCallback, ReactNode } from 'react';
import { RadioProps, Radio } from '@cimpress/react-components';

export interface RadioGroupProps<T> {
  valueSelected?: T;
  defaultSelected?: T;
  style?: CSSProperties;
  className?: string;
  inline?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>, value: T) => void;
  children?: ReactNode;
}

interface Radio {
  props: RadioProps;
  type: {
    displayName: 'CimRadio';
  };
}

function isRadio(u: unknown): u is Radio {
  return typeof u === 'object' &&
    u !== null &&
    Reflect.has(u, 'type') &&
    Reflect.get(Reflect.get(u, 'type'), 'displayName') === 'CimRadio';
}

// tslint:disable-next-line:function-name
export function RadioGroup<T>({
  valueSelected,
  defaultSelected,
  style,
  className,
  inline = false,
  onChange,
  children,
}: RadioGroupProps<T>) {
  const [value, setValue] = useState(valueSelected || defaultSelected);

  useEffect(
    () => {
      setValue(valueSelected || defaultSelected);
    },
    [valueSelected, defaultSelected],
  );

  const handleChange = useCallback(
    (event, selectedItem) => {
      setValue(selectedItem);
      if (onChange) onChange(event, selectedItem);
    },
    [onChange],
  );

  const composedStyle: CSSProperties = inline
    ? { ...style, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }
    : { ...style };

  const options = React.Children.map(
    children,
    (option: ReactNode) =>
      isRadio(option) ?
        <Radio
          {...option.props}
          name={option.props.name}
          key={option.props.value}
          value={option.props.value}
          label={option.props.label}
          onCheck={handleChange}
          checked={option.props.value === value}
          style={{ marginRight: '20px', ...option.props.style }}
        /> :
        option,
  );

  return (
    <div style={composedStyle} className={className}>
      {options}
    </div>
  );
}
